import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Search, MapPin } from "lucide-react";
import { JobSiteFormDialog } from "@/components/job-sites/JobSiteFormDialog";
import { useFilterByOwnerCompany } from "@/owner/OwnerCompanyContext";
import { CompanyBadge } from "@/owner/CompanyBadge";
import { useAuth } from "@/auth/AuthProvider";

export const Route = createFileRoute("/_app/job-sites/")({
  component: JobSitesPage,
});

type Row = {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  address_line1: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  is_active: boolean;
  project_id: string;
  company_id: string;
  projects: { id: string; code: string; name: string } | null;
};

function JobSitesPage() {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  // Supervisor vê a lista, mas só admin/owner cria ou edita (view-only para supervisor).
  const canManage = hasRole("admin");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("job_sites")
      .select(
        "id, name, city, state, address_line1, contact_name, contact_phone, is_active, project_id, company_id, projects(id, code, name)",
      )
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows((data as unknown as Row[]) ?? []);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  const scopedRows = useFilterByOwnerCompany(rows);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scopedRows;
    return scopedRows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.city ?? "").toLowerCase().includes(q) ||
        (r.address_line1 ?? "").toLowerCase().includes(q) ||
        (r.projects?.name ?? "").toLowerCase().includes(q),
    );
  }, [scopedRows, search]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("pages.jobSites.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("pages.jobSites.subtitle")}</p>
        </div>
        {canManage && (
          <Button onClick={() => setOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("pages.jobSites.new")}
          </Button>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t("pages.jobSites.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="rounded-lg border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("pages.jobSites.headers.name")}</TableHead>
              <TableHead>{t("pages.jobSites.headers.project")}</TableHead>
              <TableHead>{t("pages.jobSites.headers.address")}</TableHead>
              <TableHead>{t("pages.jobSites.headers.contact")}</TableHead>
              <TableHead>{t("pages.jobSites.headers.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-16 text-center">
                  <div className="mx-auto flex max-w-sm flex-col items-center gap-2 text-muted-foreground">
                    <MapPin className="h-8 w-8" />
                    <p className="text-sm">{t("pages.jobSites.empty")}</p>
                    {canManage && (
                      <Button size="sm" onClick={() => setOpen(true)}>
                        {t("pages.jobSites.addFirst")}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <CompanyBadge companyId={r.company_id} />
                      <Link
                        to="/job-sites/$jobSiteId"
                        params={{ jobSiteId: r.id }}
                        className="font-medium hover:underline"
                      >
                        {r.name}
                      </Link>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {r.projects ? (
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: r.projects.id }}
                        className="hover:underline"
                      >
                        <span className="font-mono text-xs">{r.projects.code}</span> —{" "}
                        {r.projects.name}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {[r.address_line1, r.city, r.state].filter(Boolean).join(", ") || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {r.contact_name ? (
                      <span>
                        {r.contact_name}
                        {r.contact_phone ? ` · ${r.contact_phone}` : ""}
                      </span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={r.is_active ? "default" : "secondary"}>
                      {r.is_active ? t("common.active") : t("common.inactive")}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <JobSiteFormDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
